import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, DrawerStepper, Text } from '@mercai/clever'

import {
  CatalogProductProps,
  GeneratePurchaseOrderPayloadAdditionalProductProps,
} from '@/types'

import { SelectCatalogProduct } from '@/components'
import { useValidationErrors } from '@/helpers'

import { POSupplierProductForm, POSupplierProductList } from '../forms'
import { schemaPOAdditionalProduct } from '../forms/po-additional-product-form/po-additional-product-form.constants'

import { DrawerAdditionalProductProps } from './drawer-additional-product.type'

import styles from './drawer-additional-product.module.css'

export const DrawerAdditionalProducts = ({
  isOpen,
  onClose,
  additionalProducts = [],
  setAdditionalProducts,
  supplierUuid,
  currency,
}: DrawerAdditionalProductProps) => {
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const [
    selectedCatalogProductProductValues,
    setSelectedCatalogProductProductValues,
  ] = useState<CatalogProductProps>()

  const [poAdditionalProductValues, setPoAdditionalProductValues] = useState<
    Partial<GeneratePurchaseOrderPayloadAdditionalProductProps>
  >({
    deliveryOption: 'keep_at_supplier',
  })

  const handleClose = () => {
    onClose()
    setSelectedCatalogProductProductValues(undefined)
  }

  const onDeleteAdditionalProduct = (catalogProductUuid: string) => {
    const newAdditionalProducts = additionalProducts?.filter(
      (product) => product?.catalogProductUuid !== catalogProductUuid,
    )

    setAdditionalProducts(newAdditionalProducts || [])
  }

  const onSelectSupplierProduct = (
    catalogProduct: CatalogProductProps,
    onNavigateToNextStep: (val: string) => void,
  ) => {
    setSelectedCatalogProductProductValues(catalogProduct)

    setPoAdditionalProductValues({
      catalogProductUuid: catalogProduct?.uuid || '',
      catalogProduct,
    })

    onNavigateToNextStep('supplier_product_form')
  }

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaPOAdditionalProduct(t),
    poAdditionalProductValues,
  )

  const onHandleSubmitPOSupplierProduct = (
    onNavigateToNextStep: (val: string) => void,
  ) => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    if (
      additionalProducts?.find(
        (prod) =>
          prod?.catalogProduct?.uuid ===
          poAdditionalProductValues?.catalogProductUuid,
      )
    ) {
      setAdditionalProducts([
        ...additionalProducts?.filter(
          (prod) =>
            prod?.catalogProduct?.uuid !==
            poAdditionalProductValues?.catalogProductUuid,
        ),
        poAdditionalProductValues,
      ])
    } else {
      setAdditionalProducts([...additionalProducts, poAdditionalProductValues])
    }

    onNavigateToNextStep('root')
  }

  const onEditSupplierProduct = (
    additionalProduct: Partial<GeneratePurchaseOrderPayloadAdditionalProductProps>,
    onNavigateToNextStep: (val: string) => void,
  ) => {
    setSelectedCatalogProductProductValues(additionalProduct?.catalogProduct)

    setPoAdditionalProductValues({
      ...additionalProduct,
    })

    onNavigateToNextStep('supplier_product_form')
  }

  return (
    <DrawerStepper
      isOpen={isOpen}
      onHandleOpen={handleClose}
      initialStep="root"
      translations={{ optional: '' }}
      title={t('componentDrawerAdditionalProductsTitle')}
      description={t('componentDrawerAdditionalProductsDescription')}
      steps={{
        root: {
          backNavigate: 'root',
          isOptional: false,
          component: ({ onNavigateToNextStep }) => (
            <div>
              <POSupplierProductList
                onClickNewProduct={() =>
                  onNavigateToNextStep('select_catalog_product')
                }
                onClickRemoveProduct={({ catalogProductUuid }) =>
                  onDeleteAdditionalProduct?.(catalogProductUuid || '')
                }
                onSelectedProduct={(val) =>
                  onEditSupplierProduct(val, onNavigateToNextStep)
                }
                additionalProductItems={additionalProducts || []}
              />
            </div>
          ),
          footer: (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={handleClose}>
                {t('closeButton')}
              </Button>
            </div>
          ),
        },
        select_catalog_product: {
          backNavigate: 'root',
          isOptional: false,
          component: ({ onNavigateToNextStep }) => (
            <div>
              <div className={styles['select-supplier-product-box']}>
                <Text variant="title-medium">
                  {t('componentDrawerSaleOrdersSelectSaleOrdersTitle')}
                </Text>
              </div>
              <SelectCatalogProduct
                onSelectProductItem={(val) =>
                  onSelectSupplierProduct(val, onNavigateToNextStep)
                }
                productsItem={
                  selectedCatalogProductProductValues
                    ? [selectedCatalogProductProductValues]
                    : []
                }
              />
            </div>
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('backButton')}
              </Button>
            </div>
          ),
        },
        supplier_product_form: {
          backNavigate: 'select_catalog_product',
          isOptional: false,
          component: () => (
            <div>
              <POSupplierProductForm
                currency={currency}
                values={poAdditionalProductValues}
                onChangeValues={setPoAdditionalProductValues}
                selectCatalogProduct={selectedCatalogProductProductValues}
                supplierUuid={supplierUuid}
                errors={errors}
              />
            </div>
          ),
          footer: ({ onNavigateToPreviousStep, onNavigateToNextStep }) => (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('backButton')}
              </Button>
              <Button
                onClick={() =>
                  onHandleSubmitPOSupplierProduct(onNavigateToNextStep)
                }
              >
                {t('confirmButton')}
              </Button>
            </div>
          ),
        },
      }}
    />
  )
}
